import el from "@cypherpotato/el";
import { Button, PrimaryButton } from "../../components/button";
import { Icon } from "../../components/icon";
import { ModelIcon } from "../../components/model-icon";
import { ModelPickerControl } from "../../components/model-picker";
import { displayFormControl } from "../../ui/alert";
import { runOperation } from "../../ui/operation";
import { getProviderColor } from "../../ui/provider-colors";
import { showErrorToast, showSuccessToast } from "../../ui/toast";

const StorageKey = "modelTiers.v2";

const Tiers = [
    { id: "s", label: "S", color: "#ffc526" },
    { id: "a", label: "A", color: "#43f425" },
    { id: "b", label: "B", color: "#32ace0" },
    { id: "c", label: "C", color: "#d55d50" },
    { id: "sad", label: ":(", color: "#cf5ebe" },
    { id: "unranked", label: "+", color: "#7b7f8c" }
];

function readSavedItems() {
    try {
        const saved = JSON.parse(localStorage.getItem(StorageKey));
        return Array.isArray(saved) ? normalizeItems(saved) : [];
    } catch {
        return [];
    }
}

function normalizeItems(items) {
    return items.map(item => {
        return {
            ...item,
            name: item.name || shortModelName(item.modelName || ""),
            modelName: item.modelName || item.name
        };
    });
}

function writeSavedItems(items) {
    localStorage.setItem(StorageKey, JSON.stringify(items));
}

function itemColor(item) {
    return getProviderColor(item.modelName || item.name);
}

function itemTextColor(item) {
    const color = itemColor(item).replace("#", "");
    const normalized = color.length === 3
        ? color.split("").map(part => part + part).join("")
        : color;
    const red = parseInt(normalized.slice(0, 2), 16);
    const green = parseInt(normalized.slice(2, 4), 16);
    const blue = parseInt(normalized.slice(4, 6), 16);
    const luminance = (red * 299 + green * 587 + blue * 114) / 1000;

    return luminance > 150 ? "#050505" : "#ffffff";
}

function shortModelName(name) {
    return name.replace(/^@.*\//, "").replace(/^.*\//, "");
}

function createItem(modelName) {
    return {
        id: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`,
        name: shortModelName(modelName),
        modelName,
        tierId: "unranked"
    };
}

function serializeRows(items) {
    return Tiers
        .filter(tier => tier.id !== "unranked")
        .map(tier => ({
            ...tier,
            items: items.filter(item => item.tierId === tier.id)
        }));
}

async function copyTierListImage(items) {
    const rows = serializeRows(items);
    const scale = Math.max(2, window.devicePixelRatio || 1);
    const labelWidth = 108;
    const rowHeight = 108;
    const gap = 14;
    const paddingX = 16;
    const chipPaddingX = 14;
    const chipHeight = 50;
    const font = "28px Arial, sans-serif";
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const icons = await loadModelIcons(items);

    ctx.font = font;

    const contentWidth = Math.max(872, Math.ceil(document.querySelector(".model-tier-board")?.clientWidth || 980));
    const rowLayouts = rows.map(row => layoutImageRow(ctx, row.items, {
        contentWidth,
        labelWidth,
        paddingX,
        chipPaddingX,
        chipHeight,
        rowHeight,
        gap
    }));
    const contentHeight = rowLayouts.reduce((height, layout) => height + layout.height, 0);

    canvas.width = contentWidth * scale;
    canvas.height = contentHeight * scale;
    canvas.style.width = `${contentWidth}px`;
    canvas.style.height = `${contentHeight}px`;
    ctx.scale(scale, scale);

    ctx.fillStyle = "#242424";
    ctx.fillRect(0, 0, contentWidth, contentHeight);
    ctx.textBaseline = "middle";

    let y = 0;
    rows.forEach((row, index) => {
        const layout = rowLayouts[index];
        ctx.fillStyle = row.color;
        ctx.fillRect(0, y, labelWidth, layout.height);

        ctx.fillStyle = "#050505";
        ctx.font = "700 34px Arial, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(row.label, labelWidth / 2, y + layout.height / 2);

        ctx.strokeStyle = "#101010";
        ctx.lineWidth = 2;
        if (index > 0) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(contentWidth, y);
            ctx.stroke();
        }

        layout.chips.forEach(({ item, x, chipY, chipWidth }) => {
            ctx.font = font;
            ctx.textAlign = "left";

            ctx.fillStyle = itemColor(item);
            ctx.fillRect(x, y + chipY, chipWidth, chipHeight);
            ctx.strokeStyle = "#050505";
            ctx.lineWidth = 2;
            ctx.strokeRect(x, y + chipY, chipWidth, chipHeight);

            ctx.fillStyle = itemTextColor(item);
            const icon = icons.get(item.id);
            if (icon) {
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(x + chipPaddingX, y + chipY + 12, 24, 24);
                ctx.drawImage(icon, x + chipPaddingX + 3, y + chipY + 15, 18, 18);
            }
            ctx.fillStyle = itemTextColor(item);
            ctx.fillText(item.name, x + chipPaddingX + 28, y + chipY + chipHeight / 2 + 1);
        });

        y += layout.height;
    });

    const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/png"));
    if (!blob || !navigator.clipboard?.write || !window.ClipboardItem) {
        throw new Error("Image clipboard is not available in this browser.");
    }

    await navigator.clipboard.write([
        new ClipboardItem({
            "image/png": blob
        })
    ]);
}

function layoutImageRow(ctx, items, options) {
    const availableWidth = Math.max(120, options.contentWidth - options.labelWidth - options.paddingX * 2);
    const chips = [];
    let lineWidth = 0;
    let lineIndex = 0;

    items.forEach(item => {
        const chipWidth = Math.ceil(ctx.measureText(item.name).width) + options.chipPaddingX * 2 + 28;

        if (lineWidth > 0 && lineWidth + options.gap + chipWidth > availableWidth) {
            lineIndex++;
            lineWidth = 0;
        }

        const x = options.labelWidth + options.paddingX + lineWidth + (lineWidth > 0 ? options.gap : 0);
        chips.push({
            item,
            x,
            chipY: options.paddingX + lineIndex * (options.chipHeight + options.gap),
            chipWidth
        });

        lineWidth += (lineWidth > 0 ? options.gap : 0) + chipWidth;
    });

    const lineCount = Math.max(1, lineIndex + 1);
    const wrappedHeight = options.paddingX * 2 + lineCount * options.chipHeight + (lineCount - 1) * options.gap;

    return {
        chips,
        height: Math.max(options.rowHeight, wrappedHeight)
    };
}

async function loadModelIcons(items) {
    const entries = await Promise.all(items.map(item =>
        new Promise(resolve => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve([item.id, image]);
            image.onerror = () => resolve([item.id, null]);
            image.src = modelIconSource(item.modelName);
        })
    ));

    return new Map(entries.filter(([, image]) => image));
}

function modelIconSource(modelName) {
    const provider = String(modelName || "").startsWith("@")
        ? String(modelName).split("/")[0].replace("@", "")
        : String(modelName || "").split("/")[0];

    return `/assets/providers/${provider || "unknown"}.svg`;
}

function openModelPicker(addModel) {
    const picker = ModelPickerControl();
    displayFormControl({
        title: "Choose model",
        message: "Select a model to add to the tier list.",
        formControlItem: picker,
        dialogSize: "large",
        validate: [
            [value => !!value, "Choose a model."]
        ],
        onConfirm(modelName) {
            addModel(modelName);
        }
    });
}

export default function () {
    const items = el.state.create(readSavedItems());

    items.subscribe(writeSavedItems);

    function addModel(name) {
        const modelName = String(name || "").trim();
        if (!modelName) {
            return;
        }

        if (items.value.some(item => String(item.modelName || item.name).toLowerCase() === modelName.toLowerCase())) {
            showErrorToast("This model is already in the tier list.");
            return;
        }

        items.value = [...items.value, createItem(modelName)];
    }

    function moveItem(itemId, tierId) {
        items.value = items.value.map(item => item.id === itemId ? { ...item, tierId } : item);
    }

    function removeItem(itemId) {
        items.value = items.value.filter(item => item.id !== itemId);
    }

    function startPointerDrag(event, item) {
        if (event.button !== 0 || event.target.closest(".model-tier-remove")) {
            return;
        }

        event.preventDefault();

        const source = event.currentTarget;
        const ghost = source.cloneNode(true);
        let activeDropzone = null;

        source.classList.add("dragging");
        ghost.classList.add("model-tier-drag-ghost");
        document.body.appendChild(ghost);

        function positionGhost(pointerEvent) {
            ghost.style.left = `${pointerEvent.clientX}px`;
            ghost.style.top = `${pointerEvent.clientY}px`;
        }

        function setActiveDropzone(dropzone) {
            if (activeDropzone === dropzone) {
                return;
            }

            activeDropzone?.classList.remove("drag-over");
            activeDropzone = dropzone;
            activeDropzone?.classList.add("drag-over");
        }

        function onPointerMove(pointerEvent) {
            positionGhost(pointerEvent);
            const dropzone = document
                .elementFromPoint(pointerEvent.clientX, pointerEvent.clientY)
                ?.closest(".model-tier-dropzone");

            setActiveDropzone(dropzone);
        }

        function onPointerUp(pointerEvent) {
            document.removeEventListener("pointermove", onPointerMove);
            document.removeEventListener("pointerup", onPointerUp);
            document.removeEventListener("pointercancel", onPointerUp);

            const dropzone = document
                .elementFromPoint(pointerEvent.clientX, pointerEvent.clientY)
                ?.closest(".model-tier-dropzone");

            const tierId = dropzone?.dataset.tierId || activeDropzone?.dataset.tierId;

            activeDropzone?.classList.remove("drag-over");
            source.classList.remove("dragging");
            ghost.remove();

            if (tierId) {
                moveItem(item.id, tierId);
            }
        }

        positionGhost(event);
        document.addEventListener("pointermove", onPointerMove);
        document.addEventListener("pointerup", onPointerUp);
        document.addEventListener("pointercancel", onPointerUp);
    }

    function itemChip(item) {
        return el(".model-tier-chip",
            el(".model-tier-provider-icon", ModelIcon(item.modelName || item.name)),
            el("span", item.name),
            el("button.model-tier-remove[type=button]",
                Icon("close-line"),
                {
                    title: "Remove model",
                    onClick(event) {
                        event.stopPropagation();
                        removeItem(item.id);
                    }
                }
            ),
            {
                style: {
                    backgroundColor: itemColor(item),
                    color: itemTextColor(item)
                },
                onPointerDown(event) {
                    startPointerDrag(event, item);
                }
            }
        );
    }

    function tierRow(tier) {
        return el(".model-tier-row",
            el(".model-tier-label", tier.label, {
                style: { backgroundColor: tier.color }
            }),
            el(".model-tier-dropzone",
                items.value.filter(item => item.tierId === tier.id).map(itemChip),
                {
                    dataTierId: tier.id
                }
            )
        );
    }

    return el(".model-tiers-page",
        el(".model-tiers-shell",
            el(".model-tiers-toolbar",
                el(".model-tiers-title",
                    el("h1", "Model tiers")
                ),
                el(".model-tiers-actions",
                    PrimaryButton(Icon("add-line"), "Add", {
                        onClick() {
                            openModelPicker(addModel);
                        }
                    }),
                    Button("Copy", {
                        onClick() {
                            runOperation("Copying tier list...", async () => {
                                await copyTierListImage(items.value);
                                showSuccessToast("Tier list image copied.");
                            });
                        }
                    })
                )
            ),
            el.state.listening([items], () =>
                el(".model-tier-board",
                    Tiers.map(tierRow)
                )
            )
        )
    );
}
